/**
 * Boîtage — choisir une zone (communes) ou « autour de moi », voir sur la carte les maisons du décile 1 (et 2-3), les DPE récents,
 * les maisons déjà boîtées et déjà connues, puis suivre la distribution en temps réel (GPS) : le parcours est enregistré,
 * chaque maison à moins de 30 m du parcours est comptée boîtée, et la session est consignée dans le journal (zone, jour, durée, km, maisons).
 * Sources : /api/v2/boitage/*, /api/v2/objectifs/communes, /api/v2/commerciaux
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Chip, TextField, Button, CircularProgress, Alert, Grid, Autocomplete, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';
const auth = () => { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}` } : {}; };

function useLeaflet() {
  // Chargement idempotent : un seul script Leaflet même si l'effet est exécuté deux fois (mode développement de React) ;
  // sinon deux copies de la bibliothèque coexistent et les marqueurs créés avec l'une ne s'affichent pas sur une carte créée avec l'autre.
  const [L, setL] = useState(window.L || null);
  useEffect(() => {
    if (window.L) { setL(window.L); return undefined; }
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link'); link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
    }
    let script = document.getElementById('leaflet-js');
    if (!script) {
      script = document.createElement('script'); script.id = 'leaflet-js'; script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      document.head.appendChild(script);
    }
    const onload = () => setL(window.L);
    script.addEventListener('load', onload);
    return () => script.removeEventListener('load', onload);
  }, []);
  return L;
}

// Couleur d'une maison selon son décile, son DPE et la date de son dernier boîtage rapportée à l'intervalle choisi :
// boîtée depuis moins des 3/4 de l'intervalle = gris (trop tôt), entre 3/4 et 1 intervalle = gris clair (bientôt), au-delà = à refaire (couleur normale)
const COULEUR = (m, intervalleJours) => {
  if (m.boitee_le) {
    const jours = (Date.now() - new Date(m.boitee_le)) / 86400000;
    if (jours < intervalleJours * 0.75) return '#9e9e9e';
    if (jours < intervalleJours) return '#bdbdbd';
  }
  if (m.dpe_recent) return '#6a1b9a';
  if (m.decile === 1) return '#c62828';
  if (m.decile === 2) return '#e65100';
  return '#f9a825';
};
const LEGENDE = (n) => [['#c62828', 'Décile 1'], ['#e65100', 'Décile 2'], ['#f9a825', 'Décile 3'], ['#6a1b9a', 'DPE récent (12 mois)'], ['#9e9e9e', `Boîtée il y a moins de ${Math.round(n * 0.75 / 7)} sem. (trop tôt)`], ['#bdbdbd', 'Bientôt à refaire'], ['#2e7d32', 'Boîtée pendant cette session']];
const STATUT_PLAN = { a_refaire: { label: 'À refaire', bg: '#ffebee', color: '#c62828' }, bientot: { label: 'Bientôt', bg: '#fff3e0', color: '#e65100' }, a_jour: { label: 'À jour', bg: '#e8f5e9', color: '#2e7d32' } };
const fmtDuree = (ms) => { const m = Math.floor(ms / 60000); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`; };

export default function Boitage({ user }) {
  const L = useLeaflet();
  const mapRef = useRef(null); const mapEl = useRef(null); const layerRef = useRef(null); const markersRef = useRef({}); const traceRef = useRef(null); const meRef = useRef(null);
  const [mode, setMode] = useState('communes');
  const [communes, setCommunes] = useState([]);
  const [selection, setSelection] = useState([]);
  const [rayon, setRayon] = useState(500);
  const [position, setPosition] = useState(null);
  const [maisons, setMaisons] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [couvertes, setCouvertes] = useState(new Set());
  const [stats, setStats] = useState({ distance_m: 0, maisons_couvertes: 0, decile_1_couvertes: 0 });
  const [tick, setTick] = useState(0);
  const [journal, setJournal] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [intervalle, setIntervalle] = useState(() => { try { return Number(localStorage.getItem('boitage_intervalle')) || 56; } catch (e) { return 56; } });
  const [planning, setPlanning] = useState([]);
  const [relecture, setRelecture] = useState(null);
  const [filtrePlan, setFiltrePlan] = useState('');
  const [importCible, setImportCible] = useState('nouvelle');
  const [importEnCours, setImportEnCours] = useState(false);
  const fichierRef = useRef(null);
  const relectureRef = useRef([]);
  const [notes, setNotes] = useState('');
  const watchRef = useRef(null); const bufferRef = useRef([]); const timerRef = useRef(null); const wakeRef = useRef(null);

  // Communes du secteur et zones du commercial
  useEffect(() => {
    (async () => {
      try {
        const [c, k] = await Promise.all([
          axios.get(`${API_BASE}/v2/objectifs/communes?tous=1`, { headers: auth(), timeout: 30000 }),
          axios.get(`${API_BASE}/v2/commerciaux`, { headers: auth(), timeout: 20000 }),
        ]);
        const moi = k.data.find(x => x.email === (user?.email || '').toLowerCase());
        // communes du secteur + zones du commercial hors secteur (ajoutées à la liste)
        const liste = [...c.data];
        (moi?.zones || []).forEach(z => { if (!liste.some(x => x.code_commune_insee === z.code_commune_insee)) liste.push({ code_commune_insee: z.code_commune_insee, commune: z.commune, code_postal: z.code_postal, decile_1: '?', secteur: false }); });
        setCommunes(liste);
        if (moi && moi.zones?.length) setSelection(liste.filter(x => moi.zones.some(z => z.code_commune_insee === x.code_commune_insee)));
      } catch (e) { /* silencieux */ }
    })();
    chargerJournal();
  }, [user]);

  const chargerJournal = async () => {
    try { const r = await axios.get(`${API_BASE}/v2/boitage/sessions?jours=365&limit=200`, { headers: auth(), timeout: 20000 }); setJournal(r.data.sessions); } catch (e) { /* */ }
  };
  const chargerPlanning = useCallback(async () => {
    try { const r = await axios.get(`${API_BASE}/v2/boitage/planning?intervalle_jours=${intervalle}`, { headers: auth(), timeout: 30000 }); setPlanning(r.data.communes); } catch (e) { /* */ }
  }, [intervalle]);
  useEffect(() => { chargerPlanning(); try { localStorage.setItem('boitage_intervalle', String(intervalle)); } catch (e) { /* */ } }, [chargerPlanning, intervalle]);

  // Relecture d'une tournée passée : trace et maisons couvertes dessinées sur la carte
  const revoir = async (s) => {
    try {
      const r = await axios.get(`${API_BASE}/v2/boitage/sessions/${s.id}`, { headers: auth(), timeout: 30000 });
      const L = window.L; if (!L || !mapRef.current) return;
      relectureRef.current.forEach(l => { try { l.remove(); } catch (e) { /* */ } }); relectureRef.current = [];
      const trace = r.data.trace.map(p => [p.lat, p.lon]);
      if (trace.length) { const pl = L.polyline(trace, { color: '#6a1b9a', weight: 4, opacity: 0.85, dashArray: '6 6' }).addTo(mapRef.current); relectureRef.current.push(pl); }
      const pts = [...trace];
      r.data.maisons.forEach(m => { const mk = L.circleMarker([m.lat, m.lon], { radius: 6, color: 'white', weight: 1.5, fillColor: '#2e7d32', fillOpacity: 0.95 }).bindPopup(`<b>${m.adresse || ''}</b><br/>${m.commune || ''}<br/>Boîtée le ${new Date(m.ts).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}${m.decile ? ` · décile ${m.decile}` : ''}`).addTo(mapRef.current); relectureRef.current.push(mk); pts.push([m.lat, m.lon]); });
      if (pts.length) vue(() => mapRef.current.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 17, animate: false }));
      setRelecture({ ...r.data.session, nb: r.data.maisons.length, points: r.data.trace.length });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const fermerRelecture = () => { relectureRef.current.forEach(l => { try { l.remove(); } catch (e) { /* */ } }); relectureRef.current = []; setRelecture(null); };

  // Import d'un parcours GPX (montre, Strava, application GPS) : fusion dans une tournée récente ou nouvelle tournée
  const mesTourneesRecentes = journal.filter(s => s.agent === (user?.email || '').toLowerCase() && (Date.now() - new Date(s.debut)) < 4 * 86400000);
  const importer = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return;
    setImportEnCours(true); setError('');
    try {
      const fd = new FormData(); fd.append('fichier', f);
      if (importCible !== 'nouvelle') fd.append('session_id', importCible);
      const r = await axios.post(`${API_BASE}/v2/boitage/import`, fd, { headers: { ...auth(), 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      window.alert(`Parcours importé : ${r.data.points_importes} points, ${r.data.maisons_couvertes} maisons boîtées au total (dont ${r.data.decile_1_couvertes} du décile 1), ${(r.data.distance_m / 1000).toFixed(1)} km.`);
      await chargerJournal(); chargerPlanning();
      revoir(r.data.session);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setImportEnCours(false); }
  };

  const localiser = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Géolocalisation non disponible'));
    navigator.geolocation.getCurrentPosition(p => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }), e => reject(new Error('Position refusée ou indisponible')), { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 });
  });

  const charger = async () => {
    setLoading(true); setError('');
    try {
      let url;
      if (mode === 'gps') {
        const p = await localiser(); setPosition(p);
        url = `${API_BASE}/v2/boitage/maisons?lat=${p.lat}&lon=${p.lon}&rayon_m=${rayon}`;
      } else {
        if (!selection.length) { setError('Choisissez au moins une commune.'); setLoading(false); return; }
        url = `${API_BASE}/v2/boitage/maisons?communes=${selection.map(s => s.code_commune_insee).join(',')}`;
      }
      const r = await axios.get(url, { headers: auth(), timeout: 60000 });
      setMaisons(r.data.maisons);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  // Carte
  // La carte n'est créée que lorsque son conteneur a une taille (sinon Leaflet plante) ; on réessaie tant que ce n'est pas le cas
  useEffect(() => {
    if (!L || mapRef.current) return undefined;
    const creer = () => {
      const el = mapEl.current;
      if (!el || !el.clientWidth || !el.clientHeight) return false;
      try {
        mapRef.current = L.map(el, { zoomControl: true, zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }).setView([49.92, 1.08], 11, { animate: false });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(mapRef.current);
        layerRef.current = L.layerGroup().addTo(mapRef.current);
        setMapReady(true);
      } catch (e) { console.warn('Carte non initialisée :', e.message); }
      return true;
    };
    if (creer()) return undefined;
    const t = setInterval(() => { if (creer()) clearInterval(t); }, 500);
    return () => clearInterval(t);
  }, [L]);
  // Vue sûre : ne plante jamais si le conteneur n'a pas encore de taille (téléphone au premier affichage)
  const vue = (fn) => { try { mapRef.current.invalidateSize(); fn(); } catch (e) { setTimeout(() => { try { mapRef.current.invalidateSize(); fn(); } catch (err) { /* ignoré */ } }, 600); } };
  useEffect(() => {
    if (!L || !mapReady || !mapRef.current || !maisons) return;
    layerRef.current.clearLayers(); markersRef.current = {};
    const pts = [];
    maisons.forEach(m => {
      if (!m.latitude || !m.longitude) return;
      const c = couvertes.has(m.id) ? '#2e7d32' : COULEUR(m, intervalle);
      const mk = L.circleMarker([m.latitude, m.longitude], { radius: m.decile === 1 || m.dpe_recent ? 7 : 5, color: 'white', weight: 1.5, fillColor: c, fillOpacity: 0.95 })
        .bindPopup(`<b>${m.adresse || ''}</b><br/>${m.commune || ''}<br/>${m.decile ? `Décile ${m.decile}` : 'Hors cible'}${m.score ? ` · 1 sur ${Math.round(1 / m.score)}` : ''}${m.dpe_recent ? `<br/>DPE ${m.dpe_classe || ''} du ${new Date(m.dpe_recent).toLocaleDateString('fr-FR')}` : ''}${m.boitee_le ? `<br/>Boîtée le ${new Date(m.boitee_le).toLocaleDateString('fr-FR')}` : ''}${m.deja_connu ? '<br/>Déjà dans Netty' : ''}${m.suivi_statut ? `<br/>Suivi : ${m.suivi_statut}` : ''}`);
      try { layerRef.current.addLayer(mk); } catch (e) { /* ignoré */ } markersRef.current[m.id] = mk; pts.push([m.latitude, m.longitude]);
    });
    if (pts.length && !session) vue(() => mapRef.current.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 16, animate: false }));
    if (position) {
      try {
        if (meRef.current) meRef.current.remove();
        meRef.current = L.circleMarker([position.lat, position.lon], { radius: 9, color: '#1565c0', weight: 3, fillColor: '#64b5f6', fillOpacity: 0.9 }).addTo(mapRef.current);
      } catch (e) { /* ignoré */ }
      if (mode === 'gps' && !session) vue(() => mapRef.current.setView([position.lat, position.lon], 16, { animate: false }));
    }
  }, [L, mapReady, maisons, position, intervalle]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!L || !mapReady || !mapRef.current) return;
    couvertes.forEach(id => { const mk = markersRef.current[id]; try { if (mk) mk.setStyle({ fillColor: '#2e7d32' }); } catch (e) { /* ignoré */ } });
  }, [couvertes, L, mapReady]);

  // Session de boîtage
  const envoyer = useCallback(async (sid) => {
    const pts = bufferRef.current.splice(0); if (!pts.length) return;
    try {
      const r = await axios.post(`${API_BASE}/v2/boitage/sessions/${sid}/positions`, pts, { headers: auth(), timeout: 20000 });
      setStats({ distance_m: r.data.distance_m, maisons_couvertes: r.data.maisons_couvertes, decile_1_couvertes: r.data.decile_1_couvertes });
      if (r.data.nouvelles?.length) setCouvertes(prev => { const n = new Set(prev); r.data.nouvelles.forEach(x => n.add(x)); return n; });
    } catch (e) { bufferRef.current.unshift(...pts); }
  }, []);

  const demarrer = async () => {
    setError('');
    try {
      const p = await localiser(); setPosition(p);
      const body = mode === 'gps' ? { zone_label: `Autour de ${p.lat.toFixed(4)}, ${p.lon.toFixed(4)} (${rayon} m)`, communes: [], lat: p.lat, lon: p.lon, rayon_m: rayon }
        : { zone_label: selection.map(s => s.commune).join(', '), communes: selection.map(s => s.code_commune_insee), lat: p.lat, lon: p.lon };
      const r = await axios.post(`${API_BASE}/v2/boitage/sessions`, body, { headers: auth(), timeout: 20000 });
      setSession(r.data); setCouvertes(new Set()); setStats({ distance_m: 0, maisons_couvertes: 0, decile_1_couvertes: 0 }); setNotes('');
      if (!maisons) await charger();
      if (L && mapRef.current) { try { if (traceRef.current) traceRef.current.remove(); traceRef.current = L.polyline([[p.lat, p.lon]], { color: '#1565c0', weight: 4, opacity: 0.8 }).addTo(mapRef.current); } catch (e) { /* ignoré */ } vue(() => mapRef.current.setView([p.lat, p.lon], 17, { animate: false })); }
      bufferRef.current = [{ lat: p.lat, lon: p.lon, ts: new Date().toISOString() }];
      watchRef.current = navigator.geolocation.watchPosition(pos => {
        const pt = { lat: pos.coords.latitude, lon: pos.coords.longitude, ts: new Date().toISOString() };
        bufferRef.current.push(pt); setPosition({ lat: pt.lat, lon: pt.lon });
        try { if (traceRef.current) traceRef.current.addLatLng([pt.lat, pt.lon]); if (meRef.current) meRef.current.setLatLng([pt.lat, pt.lon]); } catch (e) { /* ignoré */ }
      }, () => {}, { enableHighAccuracy: true, maximumAge: 3000, timeout: 20000 });
      timerRef.current = setInterval(() => { envoyer(r.data.id); setTick(t => t + 1); }, 10000);
      try { if (navigator.wakeLock) wakeRef.current = await navigator.wakeLock.request('screen'); } catch (e) { /* facultatif */ }
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const terminer = async () => {
    if (!session) return;
    try {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      await envoyer(session.id);
      const r = await axios.post(`${API_BASE}/v2/boitage/sessions/${session.id}/stop`, { notes: notes || null }, { headers: auth(), timeout: 20000 });
      try { wakeRef.current && wakeRef.current.release(); } catch (e) { /* */ }
      setSession(null);
      window.alert(`Boîtage terminé : ${r.data.maisons_couvertes} maisons couvertes (dont ${r.data.decile_1_couvertes} du décile 1), ${(r.data.distance_m / 1000).toFixed(1)} km.`);
      chargerJournal(); chargerPlanning(); charger();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  useEffect(() => () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); if (timerRef.current) clearInterval(timerRef.current); }, []);

  const options = useMemo(() => communes.map(c => ({ ...c, label: `${c.commune} (${c.code_postal}) · D1 : ${c.decile_1}${c.secteur === false ? ' · hors secteur' : ''}` })), [communes]);
  const resume = maisons ? { d1: maisons.filter(m => m.decile === 1).length, dpe: maisons.filter(m => m.dpe_recent).length, deja: maisons.filter(m => m.boitee_le && (Date.now() - new Date(m.boitee_le)) < intervalle * 86400000).length } : null;
  const duree = session ? fmtDuree(Date.now() - new Date(session.debut)) : '';

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={800} gutterBottom>📬 Boîtage</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Choisissez où vous boîtez, chargez la carte, puis démarrez : votre parcours est enregistré et chaque maison à moins de 30 mètres est comptée. Gardez l'écran allumé et le téléphone sur vous.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, v) => v && setMode(v)} disabled={!!session}>
              <ToggleButton value="communes">Par commune</ToggleButton>
              <ToggleButton value="gps"><MyLocationIcon fontSize="small" sx={{ mr: 0.5 }} />Autour de moi</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          {mode === 'communes' ? (
            <Grid item xs={12} sm={6}>
              <Autocomplete multiple size="small" options={options} value={selection} groupBy={(o) => o.code_postal} getOptionLabel={(o) => o.label} disabled={!!session}
                filterOptions={(opts, state) => { const q = state.inputValue.trim().toLowerCase(); if (!q) return opts.slice(0, 200); return opts.filter(o => o.label.toLowerCase().includes(q) || (o.code_postal || '').startsWith(q)).slice(0, 200); }}
                isOptionEqualToValue={(a, b) => a.code_commune_insee === b.code_commune_insee} onChange={(e, v) => setSelection(v)}
                renderInput={(p) => <TextField {...p} label="Communes à boîter" placeholder="Tapez une commune" />} />
            </Grid>
          ) : (
            <Grid item xs={6} sm={2}>
              <TextField select fullWidth size="small" label="Rayon" value={rayon} onChange={e => setRayon(Number(e.target.value))} disabled={!!session}>
                {[300, 500, 800, 1000, 1500].map(r => <MenuItem key={r} value={r}>{r} m</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6} sm={2}>
            <Tooltip title="Délai avant de repasser dans une rue. Les maisons boîtées depuis moins longtemps sont grisées sur la carte et la commune est marquée « à jour » dans le planning.">
              <TextField select fullWidth size="small" label="Repasser tous les" value={intervalle} onChange={e => setIntervalle(Number(e.target.value))}>
                {[42, 56, 84, 120].map(j => <MenuItem key={j} value={j}>{Math.round(j / 7)} semaines</MenuItem>)}
              </TextField>
            </Tooltip>
          </Grid>
          <Grid item xs="auto"><Button variant="outlined" onClick={charger} disabled={loading || !!session}>{loading ? <CircularProgress size={18} /> : 'Charger la carte'}</Button></Grid>
          <Grid item xs="auto">
            {!session
              ? <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={demarrer} disabled={loading}>Démarrer le boîtage</Button>
              : <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={terminer}>Terminer</Button>}
          </Grid>
        </Grid>
        {resume && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            <Chip label={`${maisons.length} maisons affichées`} size="small" />
            <Chip label={`Décile 1 : ${resume.d1}`} size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} />
            <Chip label={`DPE récents : ${resume.dpe}`} size="small" sx={{ bgcolor: '#ede7f6', color: '#6a1b9a', fontWeight: 700 }} />
            <Chip label={`Boîtées depuis moins de ${Math.round(intervalle / 7)} sem. : ${resume.deja}`} size="small" sx={{ bgcolor: '#eeeeee', color: '#616161', fontWeight: 700 }} />
          </Box>
        )}
        {session && (
          <Alert severity="success" icon={false} sx={{ mt: 1.5, py: 0.5 }}>
            <b>Boîtage en cours</b> · {duree} · {(stats.distance_m / 1000).toFixed(2)} km · <b>{stats.maisons_couvertes}</b> maisons couvertes, dont <b>{stats.decile_1_couvertes}</b> du décile 1
            <span style={{ display: 'none' }}>{tick}</span>
            <TextField size="small" fullWidth placeholder="Note de fin (facultatif) : rues faites, à finir…" value={notes} onChange={e => setNotes(e.target.value)} sx={{ mt: 1, bgcolor: 'white' }} />
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
          <Typography variant="body2" sx={{ color: '#555' }}>Tournée enregistrée par la montre ou Strava (écran éteint) :</Typography>
          <TextField select size="small" value={importCible} onChange={e => setImportCible(e.target.value)} sx={{ minWidth: 260 }} disabled={importEnCours}>
            <MenuItem value="nouvelle">Créer une nouvelle tournée</MenuItem>
            {mesTourneesRecentes.map(s => <MenuItem key={s.id} value={String(s.id)}>Fusionner avec : {new Date(s.debut).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · {s.zone_label}</MenuItem>)}
          </TextField>
          <input ref={fichierRef} type="file" accept=".gpx,application/gpx+xml" style={{ display: 'none' }} onChange={importer} />
          <Button variant="outlined" size="small" startIcon={importEnCours ? <CircularProgress size={14} /> : <UploadFileIcon />} onClick={() => fichierRef.current && fichierRef.current.click()} disabled={importEnCours}>Importer un parcours (GPX)</Button>
          <Tooltip title="Sur la montre ou Strava : enregistrer une marche en partant, l'arrêter en rentrant, exporter le fichier GPX (Strava : activité, menu ⋯, Exporter GPX) et le choisir ici. Les points déjà enregistrés en direct sont fusionnés sans doublon."><span style={{ color: '#999', fontSize: '0.8rem', cursor: 'help' }}>comment ?</span></Tooltip>
        </Box>
      </Paper>
      {relecture && (
        <Alert severity="info" sx={{ mb: 1 }} onClose={fermerRelecture}>
          Relecture : tournée du {new Date(relecture.debut).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} par {(relecture.agent || '').split('@')[0]} · {relecture.zone_label} · {relecture.nb} maisons boîtées, {relecture.points} points GPS. Trace en pointillés violets, maisons en vert.
        </Alert>
      )}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 1 }}>
        <Box ref={mapEl} sx={{ height: { xs: 420, md: 560 }, width: '100%' }} />
      </Paper>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, px: 0.5 }}>
        {LEGENDE(intervalle).map(([c, l]) => <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c, border: '1.5px solid white', boxShadow: '0 0 0 1px #ccc' }} />{l}</Box>)}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={800}>Planning des tournées <span style={{ fontWeight: 400, fontSize: '0.85rem', color: '#777' }}>(repasser tous les {Math.round(intervalle / 7)} semaines ; « à refaire » = moins de 30 % du décile 1 boîté dans l'intervalle)</span></Typography>
        <TextField size="small" label="Filtrer (commune ou code postal)" value={filtrePlan} onChange={e => setFiltrePlan(e.target.value)} sx={{ ml: 'auto', minWidth: 260 }} />
      </Box>
      <Paper elevation={1} sx={{ overflowX: 'auto', mb: 3 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
          <thead><tr style={{ background: '#f5f5f5' }}>{['Commune', 'CP', 'Décile 1', 'Boîtées dans l\'intervalle', 'Dernière tournée', 'À refaire le', 'Statut', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 10px' }}>{h}</th>)}</tr></thead>
          <tbody>
            {[...planning].filter(c => { const q = filtrePlan.trim().toLowerCase(); return !q || (c.commune || '').toLowerCase().includes(q) || (c.code_postal || '').startsWith(q); })
              .sort((a, b) => ({ a_refaire: 0, bientot: 1, a_jour: 2 }[a.statut] - { a_refaire: 0, bientot: 1, a_jour: 2 }[b.statut]) || (b.decile_1 - a.decile_1)).slice(0, 150).map(c => (
              <tr key={c.code_commune_insee} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: '6px 10px' }}><b>{c.commune}</b></td>
                <td style={{ padding: '6px 10px' }}>{c.code_postal}</td>
                <td style={{ padding: '6px 10px' }}>{c.decile_1}</td>
                <td style={{ padding: '6px 10px' }}>{c.d1_couvertes_intervalle} ({c.part_couverte} %)</td>
                <td style={{ padding: '6px 10px' }}>{c.derniere_tournee ? `${new Date(c.derniere_tournee).toLocaleDateString('fr-FR')} (${c.jours_depuis} j)` : 'jamais'}</td>
                <td style={{ padding: '6px 10px' }}>{c.prochaine ? new Date(c.prochaine).toLocaleDateString('fr-FR') : 'maintenant'}</td>
                <td style={{ padding: '6px 10px' }}><Chip label={STATUT_PLAN[c.statut].label} size="small" sx={{ bgcolor: STATUT_PLAN[c.statut].bg, color: STATUT_PLAN[c.statut].color, fontWeight: 700, height: 20 }} /></td>
                <td style={{ padding: '6px 10px' }}><Button size="small" disabled={!!session} onClick={() => { setMode('communes'); const o = options.find(x => x.code_commune_insee === c.code_commune_insee); if (o) setSelection([o]); window.scrollTo({ top: 0, behavior: 'smooth' }); }} sx={{ textTransform: 'none', py: 0 }}>Boîter</Button></td>
              </tr>
            ))}
            {planning.length === 0 && <tr><td colSpan={8} style={{ padding: 12, color: '#999', textAlign: 'center' }}>Chargement du planning…</td></tr>}
            {planning.length > 150 && <tr><td colSpan={8} style={{ padding: 8, color: '#777', fontSize: '0.8rem' }}>{planning.length} communes au total (Seine-Maritime et Somme), les 150 premières affichées par priorité puis par nombre de maisons en décile 1 : utilisez le filtre.</td></tr>}
          </tbody>
        </table>
      </Paper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" fontWeight={800}>Journal des boîtages</Typography>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} href={`${API_BASE}/v2/boitage/export.csv?jours=3650`} target="_blank" rel="noopener noreferrer" sx={{ ml: 'auto' }}>Export tableur</Button>
      </Box>
      <Paper elevation={1} sx={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
          <thead><tr style={{ background: '#f5f5f5' }}>{['Date', 'Agent', 'Zone', 'Durée', 'Distance', 'Maisons boîtées', 'Dont décile 1', 'Notes', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 10px' }}>{h}</th>)}</tr></thead>
          <tbody>
            {journal.length === 0 && <tr><td colSpan={9} style={{ padding: 12, color: '#999', textAlign: 'center' }}>Aucun boîtage enregistré pour l'instant.</td></tr>}
            {journal.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{new Date(s.debut).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}{!s.fin && <Chip label="en cours" size="small" color="success" sx={{ ml: 1, height: 18 }} />}</td>
                <td style={{ padding: '6px 10px' }}>{(s.agent || '').split('@')[0]}</td>
                <td style={{ padding: '6px 10px' }}>{s.zone_label}</td>
                <td style={{ padding: '6px 10px' }}>{Math.round(s.duree_min || 0)} min</td>
                <td style={{ padding: '6px 10px' }}>{((s.distance_m || 0) / 1000).toFixed(1)} km</td>
                <td style={{ padding: '6px 10px' }}><b>{s.maisons_couvertes}</b></td>
                <td style={{ padding: '6px 10px' }}>{s.decile_1_couvertes}</td>
                <td style={{ padding: '6px 10px', color: '#555' }}>{s.notes || ''}</td>
                <td style={{ padding: '6px 10px' }}><Button size="small" onClick={() => revoir(s)} sx={{ textTransform: 'none', py: 0 }}>Revoir</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>
    </Box>
  );
}
